<template>
  <div class="overflow-auto h-[70vh]">
    <v-data-table
      :headers="tableHeaders"
      :items="holidayPlans"
      :loading="loading"
      loading-text="Loading holiday plans..."
      class="border border-[#e0e0e0] table-radius mt-2"
      show-select
      disable-sort
    >
      <template v-for="(header, index) in tableHeaders" v-slot:[`header.${header.value}`]>
        <p :key="index" class="font-title" v-tooltip="{ text: header.text }">
          {{ header.text }}
        </p>
      </template>
      <template v-slot:[`item.planName`]="{ item }">
        <td class="px-4 py-3">{{ item.planName }}_{{ item.planYear }}</td>
      </template>

      <template v-slot:[`item.branchName`]="{ item }">
        <td class="px-4 py-3">{{ item.branchName }}</td>
      </template>
      <template v-slot:[`item.branchAddress`]="{ item }">
        <td class="px-4 py-3">{{ branchFullAddress(item.branch) }}</td>
      </template>
      <template v-slot:[`item.holidaysCount`]="{ item }">
        <td class="px-4 py-3">
          <div class="d-flex item-center">
            {{ formatNumber(item.listOfHolidays.length) }}
            <v-icon
              v-if="item.listOfHolidays.length > 0"
              size="22"
              left
              color="primary"
              v-tooltip="{
                text: `${getHolidayTooltip(item)}`,
                width: '300px',
                height: '400px',
                trigger: 'click',
                bottom: true
              }"
              class="ml-2 cursor-pointer"
            >
              mdi-information-variant-circle-outline
            </v-icon>
          </div>
        </td>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <td class="px-4 py-3">
          <ActionButton
            :item="item"
            :can-edit="hasIamPermission(iamStatements, 'organization.holiday.update')"
            :can-delete="hasIamPermission(iamStatements, 'organization.holiday.delete')"
            :show-edit-button="hasIamPermission(iamStatements, 'organization.holiday.update')"
            :show-delete-button="hasIamPermission(iamStatements, 'organization.holiday.delete')"
            :handle-edit="editHolidayPlan"
            :handle-delete="deleteHolidayPlan"
          />
        </td>
      </template>
    </v-data-table>

    <v-dialog v-model="drawer" max-width="900px" persistent>
      <div class="flex flex-col h-[80vh] max-h-[80vh] bg-white">
        <!-- Fixed Header -->
        <div class="sticky top-0 z-10 bg-white">
          <v-card-title class="flex justify-between border-b-2 p-4">
            <h3 class="form-text">{{ currentPlanId ? 'Edit' : 'Add' }} Holiday Plan</h3>
            <v-btn icon @click="closeDrawer">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
        </div>

        <!-- Scrollable Content -->
        <div class="flex-1 overflow-y-auto relative">
          <!-- Loader overlay -->
          <div
            v-if="dialogLoading"
            class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10"
          >
            <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
          </div>

          <div class="p-4">
            <v-form ref="form">
              <h4 class="section-title mb-4">Basic Information</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <v-text-field
                  label="Plan Name"
                  outlined
                  dense
                  hide-details
                  v-model="planName"
                  :rules="[mandatoryRule]"
                ></v-text-field>

                <v-select
                  v-model="planYear"
                  :items="availableYears"
                  label="Plan Year"
                  outlined
                  dense
                  hide-details
                  :rules="[mandatoryRule]"
                  :disabled="currentPlanId"
                  @change="onYearChange"
                ></v-select>

                <!-- <v-autocomplete
                  v-model="selectedCountry"
                  :items="countries"
                  item-text="name"
                  item-value="iso2"
                  label="Select Country"
                  outlined
                  dense
                  hide-details
                  @change="fetchHolidaysList"
                ></v-autocomplete> -->
                <v-autocomplete
                  v-model="selectedBranch"
                  :items="branches"
                  label="Select Branch"
                  item-text="branchName"
                  item-value="_id"
                  outlined
                  dense
                  hide-details
                  @change="fetchHolidaysList"
                >
                  <template v-slot:item="{ item }">
                    <div>
                      <div>{{ item.branchName }}</div>
                      <div class="text--secondary caption">{{ item.address }}</div>
                    </div>
                  </template>
                </v-autocomplete>
              </div>

              <h4 class="section-title mb-4 mt-6">Holidays</h4>
              <div class="border border-gray-300 rounded">
                <div class="flex justify-end items-center p-3 bg-gray-50">
                  <v-btn
                    x-small
                    color="primary"
                    outlined
                    @click="addHoliday"
                    class="normal-case font-medium text-xs"
                  >
                    <v-icon x-small class="mr-1">mdi-plus</v-icon>
                    Add
                  </v-btn>
                </div>

                <div class="p-3">
                  <div
                    v-for="(holiday, index) in listOfHolidays"
                    :key="index"
                    class="border-b border-gray-200 pb-4 mb-4 last:border-0 last:pb-0 last:mb-0 relative"
                  >
                    <!-- Clear button positioned at top-right corner -->
                    <div class="absolute top-[-10px] right-[-10px] mt-2 mr-2">
                      <v-btn
                        icon
                        x-small
                        color="error"
                        @click="removeHoliday(index)"
                        v-if="listOfHolidays.length > 1"
                      >
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-9 gap-3 items-center pt-4">
                      <div class="md:col-span-3">
                        <v-select
                          v-model="holiday.holidayType"
                          :items="holidayTypes"
                          label="Holiday Type"
                          outlined
                          dense
                          hide-details
                          @change="filterHolidaysByType(holiday)"
                        ></v-select>
                      </div>
                      <div class="md:col-span-3">
                        <v-select
                          v-model="holiday.holidayName"
                          label="Holiday Name"
                          :items="holiday.filteredHolidays || allHolidays"
                          item-text="name"
                          item-value="name"
                          outlined
                          dense
                          hide-details
                          :rules="[mandatoryRule]"
                          @change="setHolidayDates(holiday)"
                        ></v-select>
                      </div>
                      <div class="md:col-span-3">
                        <v-text-field
                          v-model="holiday.customName"
                          label="Custom Name (Optional)"
                          outlined
                          dense
                          hide-details
                        ></v-text-field>
                      </div>
                      <div class="md:col-span-4">
                        <v-menu
                          v-model="holiday.menuStart"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          transition="scale-transition"
                          offset-y
                          min-width="290px"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              :value="formatDate(holiday.holidayStartDate)"
                              label="From Date"
                              prepend-inner-icon="mdi-calendar"
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              outlined
                              dense
                              hide-details
                              :rules="[mandatoryRule]"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="holiday.holidayStartDate"
                            @input="holiday.menuStart = false"
                            no-title
                            scrollable
                            :min="getYearStartDate()"
                            :max="getYearEndDate()"
                          ></v-date-picker>
                        </v-menu>
                      </div>
                      <div class="md:col-span-1">
                        <p class="text-center pb-0 mb-0">- To -</p>
                      </div>
                      <div class="md:col-span-4">
                        <v-menu
                          v-model="holiday.menuEnd"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          transition="scale-transition"
                          offset-y
                          min-width="290px"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              :value="formatDate(holiday.holidayEndDate)"
                              label="Till Date"
                              prepend-inner-icon="mdi-calendar"
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              outlined
                              dense
                              hide-details
                              :rules="[mandatoryRule]"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="holiday.holidayEndDate"
                            @input="holiday.menuEnd = false"
                            no-title
                            scrollable
                            :min="getYearStartDate()"
                            :max="getYearEndDate()"
                          ></v-date-picker>
                        </v-menu>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-form>
          </div>
        </div>

        <!-- Fixed Footer -->
        <div class="sticky bottom-0 z-10 bg-white">
          <v-divider></v-divider>
          <div class="flex justify-end p-4">
            <v-btn class="normal-case" text @click="closeDrawer">Cancel</v-btn>
            <v-btn
              color="primary"
              class="normal-case"
              v-iam-disabled="'organization.holiday.update'"
              @click="saveHolidayPlan"
            >
              <v-icon left>mdi-content-save</v-icon>
              Save</v-btn
            >
          </div>
        </div>
      </div>
    </v-dialog>

    <AlertPopUp
      :showConfirmationDialog="showConfirmationDialog"
      :confirmationTitle="'Confirm Delete'"
      :confirmationMessage="'Are you sure you want to delete the holiday plan?'"
      :confirmationActionText="'Delete'"
      :confirmationActionColor="'red'"
      :performAction="confirmDelete"
      :cancel="cancelDelete"
    />
  </div>
</template>

<script>
import { convertToTitleCase, getFullAddress } from '@/utils/common'
import { hasIamPermission } from '@/utils/iamEvaluator'
import AlertPopUp from '@/components/AlertPopUp'
import ActionButton from '@/components/ActionButton.vue'

export default {
  name: 'HolidayManagement',
  components: {
    AlertPopUp,
    ActionButton
  },
  props: {
    roles: {
      type: Array,
      default: () => []
    },
    branches: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      drawer: false,
      planName: '',
      planYear: new Date().getFullYear(),
      selectedBranch: null,
      // countries: [],
      allHolidays: [],
      allHolidaysBackup: [], // Add this to keep a backup of all holidays
      availableYears: this.generateYearOptions(),
      listOfHolidays: [
        {
          holidayType: 'NATIONAL',
          holidayName: '',
          customName: '',
          holidayStartDate: '',
          holidayEndDate: '',
          menuStart: false,
          menuEnd: false
        }
      ],
      currentPlanId: null,
      showConfirmationDialog: false,
      holidayPlans: [],
      loading: true,
      templateToDelete: null,
      tableHeaders: [
        { text: 'Plan', value: 'planName', sortable: true },
        { text: 'Branch', value: 'branchName', sortable: true },
        { text: 'Address', value: 'branchAddress', sortable: true },
        { text: 'Holidays Count', value: 'holidaysCount', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      holidayTypes: [],
      // Add loading state for dialog
      dialogLoading: false
    }
  },
  computed: {
    iamStatements() {
      return this.$store.state.wfmStatements || []
    },
    mandatoryRule: () => (value) => !!value || 'This field is required',
    selectCountryCode() {
      const branchData = this.$props?.branches?.find((b) => b._id == this.selectedBranch)
      return branchData?.branchLocation?.iso || 'IN'
    }
  },
  methods: {
    hasIamPermission,
    generateYearOptions() {
      const currentYear = this.$dayjs().year()
      const years = []
      for (let year = currentYear; year <= currentYear + 1; year++) {
        years.push({ text: year.toString(), value: year })
      }
      return years
    },
    branchFullAddress(branch) {
      return getFullAddress(branch?.branchLocation)
    },
    titleCase(str) {
      return convertToTitleCase(str)
    },
    filterHolidaysByType(holiday) {
      if (!holiday.holidayType) {
        // If no type selected, show all holidays
        this.$set(holiday, 'filteredHolidays', this.allHolidays)
        return
      }

      // Filter holidays by selected type
      const filtered = this.allHolidays.filter((h) => h.primary_type === holiday.holidayType)

      // Set the filtered holidays for this specific holiday entry
      this.$set(holiday, 'filteredHolidays', filtered)
    },
    setHolidayDates(holiday) {
      if (!holiday.holidayName) return

      // Find the selected holiday in the allHolidays array
      const selectedHoliday = this.allHolidays.find((h) => h.name === holiday.holidayName)

      if (selectedHoliday && selectedHoliday.start) {
        // Extract date part from ISO string (format: YYYY-MM-DDTHH:mm:ssZ)
        const datePart = selectedHoliday.start.split('T')[0]

        // Set both start and end dates to the holiday date
        holiday.holidayStartDate = datePart
        holiday.holidayEndDate = datePart
      }
    },
    async fetchHolidaysList() {
      try {
        const params = {
          country: this.selectCountryCode || 'IN',
          year: this.planYear || '2025'
        }
        const response = await this.$axios.get(`/country-holidays`, { params })

        const holidays = response.data?.data?.holidays || []

        const uniqueTypes = new Set()
        const formattedHolidays = holidays.map((holiday) => {
          uniqueTypes.add(holiday.primary_type)
          return {
            name: holiday.name,
            primary_type: holiday.primary_type || '',
            start: holiday.date.iso
          }
        })

        this.holidayTypes = Array.from(uniqueTypes).filter((type) => type)
        this.allHolidays = formattedHolidays
        this.allHolidaysBackup = [...formattedHolidays] // Keep a backup
      } catch (error) {
        console.error('Error fetching holiday plans:', error)
      } finally {
        this.loading = false
      }
    },
    // async fetchCountriesList() {
    //   try {
    //     const response = await this.$axios.get(`/list-of-countries`)
    //     this.countries = response.data.data
    //   } catch (error) {
    //     console.error('Error fetching holiday plans:', error)
    //   } finally {
    //     this.loading = false
    //   }
    // },
    addHoliday() {
      this.listOfHolidays.push({
        holidayType: 'NATIONAL',
        holidayName: '',
        customName: '',
        holidayStartDate: '',
        holidayEndDate: '',
        menuStart: false,
        menuEnd: false,
        filteredHolidays: this.allHolidays // Initialize with all holidays
      })
    },
    removeHoliday(index) {
      this.listOfHolidays.splice(index, 1)
    },
    openCreateHolidayPlan() {
      this.resetForm()
      this.planYear = new Date().getFullYear()
      this.drawer = true
      this.$nextTick(() => {
        if (this.branches && this.branches.length > 0) {
          this.selectedBranch = this.branches[0]._id
        }
      })
      this.dialogLoading = false
    },
    closeDrawer() {
      this.drawer = false
      this.resetForm()
      // Ensure loader is hidden when closing dialog
      this.dialogLoading = false
    },
    resetForm() {
      this.planName = ''
      this.planYear = new Date().getFullYear()
      this.selectedBranch = null
      this.listOfHolidays = [
        {
          holidayType: 'NATIONAL',
          holidayName: '',
          customName: '',
          holidayStartDate: '',
          holidayEndDate: '',
          menuStart: false,
          menuEnd: false
        }
      ]
      this.currentPlanId = null
      if (this.$refs.form) {
        this.$refs.form.reset()
      }
    },
    async fetchHolidayPlans() {
      this.loading = true
      try {
        const response = await this.$axios.get('/orgSetup/org-holidays')
        this.holidayPlans = response.data?.holidays || []

        // Process holiday plans to include branch names
        this.holidayPlans = this.holidayPlans?.map((plan) => {
          return {
            ...plan,
            branchName: plan.branch?.branchName || '-'
          }
        })
      } catch (error) {
        console.error('Error fetching holiday plans:', error)
        this.$toast.error('Failed to load holiday plans')
        this.holidayPlans = []
      } finally {
        this.loading = false
      }
    },
    async saveHolidayPlan() {
      // Validate form
      if (!this.$refs.form.validate()) {
        return
      }

      // Validate required fields
      const requiredFields = [{ field: this.planName, name: 'Plan Name' }]

      for (let { field, name } of requiredFields) {
        if (!field) {
          this.$toast.error(`${name} is required`)
          return
        }
      }

      // Validate holiday dates
      for (let i = 0; i < this.listOfHolidays.length; i++) {
        const holiday = this.listOfHolidays[i]
        if (!holiday.holidayName) {
          this.$toast.error(`Holiday name is required for holiday ${i + 1}`)
          return
        }
        if (!holiday.holidayStartDate) {
          this.$toast.error(`Start date is required for holiday ${i + 1}`)
          return
        }
        if (!holiday.holidayEndDate) {
          this.$toast.error(`End date is required for holiday ${i + 1}`)
          return
        }

        // Validate that dates are within the selected year
        const startDateYear = new Date(holiday.holidayStartDate).getFullYear()
        const endDateYear = new Date(holiday.holidayEndDate).getFullYear()
        if (startDateYear !== this.planYear || endDateYear !== this.planYear) {
          this.$toast.error(`Holiday dates must be within the selected year (${this.planYear})`)
          return
        }
      }

      const payload = {
        planName: `${this.planName}`,
        planYear: Number(this.planYear),
        branch: this.selectedBranch,
        listOfHolidays: this.listOfHolidays.map((holiday) => ({
          holidayType: holiday.holidayType,
          holidayName: holiday.holidayName,
          customName: holiday.customName || null,
          holidayStartDate: holiday.holidayStartDate,
          holidayEndDate: holiday.holidayEndDate
        }))
      }

      try {
        if (this.currentPlanId) {
          const response = await this.$axios.put(
            `/orgSetup/org-holidays/${this.currentPlanId}`,
            payload
          )
          this.$toast.success(response.data.message || 'Holiday plan updated successfully')
        } else {
          const response = await this.$axios.post('/orgSetup/org-holidays', payload)
          this.$toast.success(response.data.message || 'Holiday plan created successfully')
        }

        // Close drawer and refresh data
        this.closeDrawer()
        await this.fetchHolidayPlans()
      } catch (error) {
        console.error('Error saving holiday plan:', error)
        const errorMessage = error.response?.data?.message || 'Failed to save holiday plan'
        this.$toast.error(errorMessage)
      }
    },
    async editHolidayPlan(plan) {
      // Show dialog immediately
      this.currentPlanId = plan._id
      this.drawer = true

      // Show loader
      this.dialogLoading = true

      try {
        // Set initial form values
        this.planName = plan.planName
        this.planYear = plan.planYear
        this.selectedBranch = plan.branch?._id || null

        // Load holidays list
        await this.fetchHolidaysList()

        if (plan.listOfHolidays && Array.isArray(plan.listOfHolidays)) {
          this.listOfHolidays = plan.listOfHolidays.map((holiday) => ({
            holidayType: holiday.holidayType,
            holidayName: holiday.holidayName || '',
            customName: holiday.customName || '',
            holidayStartDate: holiday.holidayStartDate || '',
            holidayEndDate: holiday.holidayEndDate || '',
            menuStart: false,
            menuEnd: false,
            filteredHolidays: this.allHolidays // Initialize with all holidays
          }))
        } else {
          this.listOfHolidays = [
            {
              holidayType: 'NATIONAL',
              holidayName: '',
              customName: '',
              holidayStartDate: '',
              holidayEndDate: '',
              menuStart: false,
              menuEnd: false,
              filteredHolidays: this.allHolidays // Initialize with all holidays
            }
          ]
        }
      } catch (error) {
        console.error('Error loading holiday plan data:', error)
        this.$toast.error('Failed to load holiday plan data')
        this.closeDrawer()
      } finally {
        // Hide loader
        this.dialogLoading = false
      }
    },
    deleteHolidayPlan(plan) {
      this.templateToDelete = plan
      this.showConfirmationDialog = true
    },
    async confirmDelete() {
      if (!this.templateToDelete) return

      try {
        const response = await this.$axios.delete(
          `/orgSetup/org-holidays/${this.templateToDelete._id}`
        )
        this.$toast.success(response.data.message || 'Holiday plan deleted successfully')
        await this.fetchHolidayPlans()
      } catch (error) {
        console.error('Error deleting holiday plan:', error)
        const errorMessage = error.response?.data?.message || 'Failed to delete holiday plan'
        this.$toast.error(errorMessage)
      } finally {
        this.cancelDelete()
        this.templateToDelete = null
      }
    },
    cancelDelete() {
      this.showConfirmationDialog = false
      this.templateToDelete = null
    },
    formatNumber(number) {
      return number < 10 ? `0${number}` : number
    },
    formatDate(dateString) {
      if (!dateString) return ''
      return this.$dayjs(dateString).format('DD/MM/YYYY')
    },
    getHolidayTooltip(item) {
      if (!item.listOfHolidays || item.listOfHolidays.length === 0) return 'No holidays'

      return item.listOfHolidays
        .map(
          (holiday, index) =>
            `<div style="text-align:left; margin-bottom: 8px; padding: 4px 0; border-bottom: 1px solid rgba(200,250,255,0.2);">
          <strong>${index + 1}. ${holiday.customName || holiday.holidayName}</strong>
          <div>${this.formatDate(holiday.holidayStartDate)} - ${this.formatDate(
              holiday.holidayEndDate
            )}</div>
        </div>`
        )
        .join('')
    },
    // New methods for date restriction
    onYearChange() {
      // Refresh holidays list when year changes
      this.fetchHolidaysList()
    },
    getYearStartDate() {
      return `${this.planYear}-01-01`
    },
    getYearEndDate() {
      return `${this.planYear}-12-31`
    }
  },
  async mounted() {
    await this.fetchHolidayPlans()
    // await this.fetchCountriesList()
  }
}
</script>

<style scoped>
.form-text {
  font-size: 1.25rem;
  padding: 0px;
  font-weight: 600;
  color: #21427d;
  margin: 0;
}
.section-title {
  font-size: 1rem;
  font-weight: 500;
  color: #21427d;
  margin-bottom: 10px;
  align-items: center;
}
.table-radius {
  border-radius: 0px !important;
}
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.v-data-table >>> .v-data-table__checkbox {
  padding-left: 5px;
}
.v-data-table >>> .v-data-table__wrapper > table > tbody > tr > td > .v-data-table__checkbox {
  text-align: center;
}
</style>
